/**
 * QUIPWA — DB factory
 *
 * The only place that decides how each database is stored. Everything else
 * receives an `IDBAdapter`.
 *
 * | Database                | IndexedDB key            | Uploaded?          |
 * |-------------------------|--------------------------|--------------------|
 * | replicated state        | `QUIPWA:state`           | yes, as _state.db  |
 * | device-local            | `QUIPWA:local`           | never              |
 * | deck content            | `QUIPWA:collection-<id>` | inside .wflash     |
 * | snapshot (remote copy)  | — (memory only)          | —                  |
 *
 * See docs/modulos/almacenamiento.md.
 */

import { SqlJsAdapter, type SqlJsAdapterOptions } from './sqljs-adapter.ts'
import { applySchema, type MigrationReport } from './migrations.ts'
import { STATE_SCHEMA } from './schema/state-migrations.ts'
import { LOCAL_SCHEMA } from './schema/local-schema.ts'
import { COLLECTION_DB_SCHEMA } from './schema/collection-schema.ts'

export type DBAdapterType = 'sqljs-idb' | 'sqljs-memory'

export interface DBFactoryResult {
  adapter: SqlJsAdapter
  engine: DBAdapterType
  migration?: MigrationReport
}

async function open(
  name: string,
  options: SqlJsAdapterOptions,
  schema: Parameters<typeof applySchema>[1] | string
): Promise<DBFactoryResult> {
  const adapter = new SqlJsAdapter(name, options)
  if (typeof schema === 'string') {
    await adapter.initialize(schema)
    return { adapter, engine: options.persistence === 'memory' ? 'sqljs-memory' : 'sqljs-idb' }
  }
  await adapter.initialize('')
  const migration = await applySchema(adapter, schema)
  return { adapter, engine: options.persistence === 'memory' ? 'sqljs-memory' : 'sqljs-idb', migration }
}

/** The replicated `_state.db`, persisted on this device. */
export async function openStateDB(options: SqlJsAdapterOptions = {}): Promise<DBFactoryResult> {
  const result = await open('state', { persistence: 'indexeddb', ...options }, STATE_SCHEMA)
  if (result.migration?.applied.length) {
    console.info('[DB] state migrated', result.migration)
  }
  return result
}

/**
 * Open a serialized `_state.db` (e.g. downloaded from Drive) **in memory only**,
 * upgraded to the current schema so it can be read with today's queries.
 * It never touches IndexedDB; close it when done.
 */
export async function openStateSnapshot(buffer: ArrayBuffer): Promise<SqlJsAdapter> {
  const adapter = new SqlJsAdapter('state-snapshot', { persistence: 'memory', lifecycleTarget: null, visibilitySource: null })
  await adapter.initialize('')
  await adapter.importFromArrayBuffer(buffer)
  await applySchema(adapter, STATE_SCHEMA)
  return adapter
}

/** An empty in-memory `_state.db` at the current schema (tests, previews). */
export async function openEmptyStateDB(): Promise<SqlJsAdapter> {
  const { adapter } = await open('state-memory', { persistence: 'memory', lifecycleTarget: null, visibilitySource: null }, STATE_SCHEMA)
  return adapter
}

/** The device-local database (never uploaded). */
export async function openLocalDB(options: SqlJsAdapterOptions = {}): Promise<DBFactoryResult> {
  return open('local', { persistence: 'indexeddb', ...options }, LOCAL_SCHEMA)
}

/** A deck's `collection.db`, persisted locally under its instance id. */
export async function openCollectionDB(deckInstanceId: string, options: SqlJsAdapterOptions = {}): Promise<DBFactoryResult> {
  return open(`collection-${deckInstanceId}`, { persistence: 'indexeddb', ...options }, COLLECTION_DB_SCHEMA)
}

/** A throwaway in-memory `collection.db` (import previews, CSV conversion). */
export async function openMemoryCollectionDB(): Promise<DBFactoryResult> {
  return open('collection-memory', { persistence: 'memory', lifecycleTarget: null, visibilitySource: null }, COLLECTION_DB_SCHEMA)
}

// ── Backwards-compatible names ───────────────────────────────────────────────

/** @deprecated use `openStateDB` */
export const createStateDB = openStateDB
/** @deprecated use `openMemoryCollectionDB` (or `openCollectionDB` for a deck) */
export const createCollectionDB = openMemoryCollectionDB
